import { bool, isObject, isNullOrUndefined, isNumber, Cast, isString, isFunction, clamp, RemirrorError, keys, isArray, invariant, isEmptyObject, object, entries, isEmptyArray, findMatches } from '@remirror/core-helpers';
import { liftListItem, wrapInList } from '@remirror/pm/schema-list';
import { EditorState, Transaction, TextSelection, Selection, NodeSelection, Plugin, PluginKey } from '@remirror/pm/state';
import { liftTarget, findWrapping } from '@remirror/pm/transform';
import { EMPTY_PARAGRAPH_NODE, EMPTY_NODE, ErrorConstant } from '@remirror/core-constants';
import { NodeType, MarkType, Node as Node$1, Mark, Schema, ResolvedPos, Fragment, DOMSerializer, DOMParser, Slice } from '@remirror/pm/model';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { InputRule } from '@remirror/pm/inputrules';

/// <reference types="node" />
/**
 * A object with flags identifying the current environment.
 *
 * @public
 */

var environment = {
  /**
   * Verifies that the environment has both a window and window.document
   */
  get isBrowser() {
    return bool( "object".document !== 'undefined' && window.navigator && window.navigator.userAgent);
  },

  /**
   * Verifies that the environment is JSDOM
   */
  get isJSDOM() {
    return environment.isBrowser && window.navigator.userAgent.includes('jsdom');
  },

  /**
   * Verifies that the environment has a nodejs process and is therefore a node environment
   */
  get isNode() {
    return typeof process !== 'undefined' && process.versions != null && process.versions.node != null;
  },

  /**
   * True when running on macOS
   */
  get isMac() {
    return environment.isBrowser && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  },

  /**
   * Verify that this is an apple device either on the client or server.
   */
  get isApple() {
    return environment.isNode ? process.platform === 'darwin' : environment.isBrowser ? /Mac|iPod|iPhone|iPad/.test(window.navigator.platform) : false;
  },

  /**
   * True when running in DEVELOPMENT environment
   */
  get isDevelopment() {
    return process.env.NODE_ENV === 'development';
  },

  /**
   * True when running unit tests
   */
  get isTest() {
    return process.env.NODE_ENV === 'test';
  },

  /**
   * True when running in PRODUCTION environment
   */
  get isProduction() {
    return process.env.NODE_ENV === 'production';
  }

};

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
/**
 * Check to see if the passed value is a NodeType.
 *
 * @param value - the value to check
 *
 * @public
 */

function isNodeType(value) {
  return isObject(value) && value instanceof NodeType;
}
/**
 * Check to see if the passed value is a MarkType.
 *
 * @param value - the value to check
 *
 * @public
 */

function isMarkType(value) {
  return isObject(value) && value instanceof MarkType;
}
/**
 * Checks to see if the passed value is a ProsemirrorNode
 *
 * @param value - the value to check
 *
 * @public
 */

function isProsemirrorNode(value) {
  return isObject(value) && value instanceof Node$1;
}
/**
 * Checks to see if the passed value is a ProsemirrorMark
 *
 * @param value - the value to check
 *
 * @public
 */

function isProsemirrorMark(value) {
  return isObject(value) && value instanceof Mark;
}
/**
 * Checks to see if the passed value is a Prosemirror Editor State
 *
 * @param value - the value to check
 *
 * @public
 */

function isEditorState(value) {
  return isObject(value) && value instanceof EditorState;
}
/**
 * Checks to see if the passed value is a Prosemirror Transaction
 *
 * @param value - the value to check
 *
 * @public
 */

function isTransaction(value) {
  return isObject(value) && value instanceof Transaction;
}
/**
 * Checks to see if the passed value is an instance of the editor schema
 *
 * @param value - the value to check
 *
 * @public
 */

function isEditorSchema(value) {
  return isObject(value) && value instanceof Schema;
}
/**
 * Predicate checking whether the selection is a TextSelection
 *
 * @param value - the value to check
 *
 * @public
 */

function isTextSelection(value) {
  return isObject(value) && value instanceof TextSelection;
}
/**
 * Predicate checking whether the value is a Selection
 *
 * @param value - the value to check
 *
 * @public
 */

function isSelection(value) {
  return isObject(value) && value instanceof Selection;
}
/**
 * Predicate checking whether the value is a ResolvedPosition.
 *
 * @param value - the value to check
 *
 * @public
 */

function isResolvedPos(value) {
  return isObject(value) && value instanceof ResolvedPos;
}
/**
 * Predicate checking whether the selection is a NodeSelection
 *
 * @param value - the value to check
 *
 * @public
 */

function isNodeSelection(value) {
  return isObject(value) && value instanceof NodeSelection;
}

/**
 * Checks that a mark is active within the selected region, or the current
 * selection point is within a region with the mark active. Used by extensions
 * to implement their active methods.
 *
 * @public
 */
function isMarkActive(parameter) {
  var stateOrTransaction = parameter.stateOrTransaction,
      type = parameter.type,
      from = parameter.from,
      to = parameter.to;
  var selection = stateOrTransaction.selection,
      doc = stateOrTransaction.doc,
      storedMarks = stateOrTransaction.storedMarks;
  var $from = selection.$from,
      empty = selection.empty;

  if (from && to) {
    try {
      return Math.max(from, to) < doc.nodeSize && doc.rangeHasMark(from, to, type);
    } catch (_unused) {
      return false;
    }
  }

  if (empty) {
    return bool(type.isInSet(storedMarks !== null && storedMarks !== void 0 ? storedMarks : $from.marks()));
  }

  return doc.rangeHasMark(selection.from, selection.to, type);
}
/**
 * Check if the specified type (NodeType) can be inserted at the current
 * selection point.
 *
 * @param state - the editor state
 * @param type - the node type
 *
 * @public
 */

function canInsertNode(state, type) {
  var $from = state.selection.$from;

  for (var depth = $from.depth; depth >= 0; depth--) {
    var index = $from.index(depth);

    try {
      if ($from.node(depth).canReplaceWith(index, index, type)) {
        return true;
      }
    } catch (_unused2) {
      return false;
    }
  }

  return false;
}
/**
 * Checks if a node looks like an empty document
 *
 * @param node - the prosemirror node
 *
 * @public
 */

function isDocNodeEmpty(node) {
  var nodeChild = node.content.firstChild;

  if (node.childCount !== 1 || !nodeChild) {
    return false;
  }

  return nodeChild.type.isBlock && !nodeChild.childCount && nodeChild.nodeSize === 2 && (isNullOrUndefined(nodeChild.marks) || nodeChild.marks.length === 0);
}
/**
 * Retrieve the attributes for a mark.
 *
 * @param state - the editor state
 * @param type - the mark type
 */

function getMarkAttributes(state, type) {
  var _state$selection = state.selection,
      from = _state$selection.from,
      to = _state$selection.to;
  var marks = [];
  state.doc.nodesBetween(from, to, node => {
    marks = [...marks, ...node.marks];
  });
  var mark = marks.find(markItem => markItem.type.name === type.name);

  if (mark) {
    return mark.attrs;
  }

  return {};
}
/**
 * Retrieve the start and end position of a mark
 *
 * @remarks
 *
 * @param pmPosition - the resolved prosemirror position
 * @param type - the mark type
 */

function getMarkRange() {
  var pmPosition = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

  if (!pmPosition || !type) {
    return false;
  }

  var start = pmPosition.parent.childAfter(pmPosition.parentOffset);

  if (!start.node) {
    return false;
  }

  var mark = start.node.marks.find((_ref) => {
    var markType = _ref.type;
    return markType === type;
  });

  if (!mark) {
    return false;
  }

  var startIndex = pmPosition.index();
  var startPos = pmPosition.start() + start.offset;

  while (startIndex > 0 && mark.isInSet(pmPosition.parent.child(startIndex - 1).marks)) {
    startIndex -= 1;
    startPos -= pmPosition.parent.child(startIndex).nodeSize;
  }

  return {
    from: startPos,
    to: startPos + start.node.nodeSize
  };
}
/**
 * Retrieves the text content from a slice
 *
 * @remarks
 * A utility that's useful for pulling text content from a slice which is
 * usually created via `selection.content()`
 *
 * @param slice - the prosemirror slice
 *
 * @public
 */

function getTextContentFromSlice(slice) {
  var _slice$content$firstC, _slice$content$firstC2;

  return (_slice$content$firstC = (_slice$content$firstC2 = slice.content.firstChild) === null || _slice$content$firstC2 === void 0 ? void 0 : _slice$content$firstC2.textContent) !== null && _slice$content$firstC !== void 0 ? _slice$content$firstC : '';
}
/**
 * Takes an empty selection and expands it out to the nearest group not matching
 * the excluded characters.
 *
 * @remarks
 *
 * Can be used to find the nearest selected word. See {@link getSelectedWord}
 *
 * @param stateOrTransaction - the editor state
 * @param exclude - the regex pattern to exclude
 * @returns false if not a text selection or if no expansion available
 *
 * @public
 */

function getSelectedGroup(stateOrTransaction, exclude) {
  if (!isTextSelection(stateOrTransaction.selection)) {
    return false;
  }

  var _stateOrTransaction$s = stateOrTransaction.selection,
      from = _stateOrTransaction$s.from,
      to = _stateOrTransaction$s.to;

  var getChar = (start, end) => getTextContentFromSlice(TextSelection.create(stateOrTransaction.doc, start, end).content());

  for (var char = getChar(from - 1, from); char && !exclude.test(char); from--, char = getChar(from - 1, from)) {// Step backwards until reaching first excluded character or empty text
    // content.
  }

  for (var _char = getChar(to, to + 1); _char && !exclude.test(_char); to++, _char = getChar(to, to + 1)) {// Step forwards until reaching the first excluded character or empty text
    // content
  }

  if (from === to) {
    return false;
  }

  return {
    from,
    to
  };
}
/**
 * Retrieves the nearest space separated word from the current selection.
 *
 * @remarks
 *
 * This always expands outward so that given: `The tw<start>o words<end>` The
 * selection would become `The <start>two words<end>`
 *
 * In other words it expands until it meets an invalid character.
 *
 * @param stateOrTransaction - the editor state
 *
 * @public
 */

function getSelectedWord(stateOrTransaction) {
  return getSelectedGroup(stateOrTransaction, /[\s\0]/);
}
/**
 * Retrieve plugin state of specified type
 *
 * @param plugin - the plugin or plugin key
 * @param state - the editor state
 *
 * @public
 */

function getPluginState(plugin, state) {
  return plugin.getState(state);
}
/**
 * Retrieve plugin meta data of specified type
 *
 * @param key - the plugin key
 * @param tr - the transaction to retrieve from
 *
 * @public
 */

function getPluginMeta(key, tr) {
  return tr.getMeta(key);
}
/**
 * Set the plugin meta data
 *
 * @param key - the plugin key
 * @param tr - the transaction
 * @param data - the data to set
 *
 * @public
 */

function setPluginMeta(key, tr, data) {
  return tr.setMeta(key, data);
}
/**
 * Get matching string from a list or single value
 *
 * @remarks
 * Get attrs can be called with a direct match string or array of string
 * matches. This method should be used to retrieve the required string.
 *
 * The index of the matched array used defaults to 0 but can be updated via the
 * second parameter.
 *
 * @param match - the match(es)
 * @param index - the zero-index point from which to start
 *
 * @public
 */

function getMatchString(match) {
  var index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return Array.isArray(match) ? match[index] : match;
}
/**
 * Checks whether the passed value is a valid dom node
 *
 * @param domNode - the dom node
 *
 * @public
 */

function isDomNode(domNode) {
  return isObject(Node) ? domNode instanceof Node : isObject(domNode) && isNumber(Cast(domNode).nodeType) && isString(Cast(domNode).nodeName);
}
/**
 * Checks for an element node like `<p>` or `<div>`.
 *
 * @param domNode - the dom node
 *
 * @public
 */

function isElementDomNode(domNode) {
  return isDomNode(domNode) && domNode.nodeType === Node.ELEMENT_NODE;
}
/**
 * Finds the closest element which matches the passed selector
 *
 * @param domNode - the dom node
 * @param selector - the selector
 *
 * @public
 */

function closestElement(domNode, selector) {
  if (!isElementDomNode(domNode)) {
    return null;
  }

  if (isNullOrUndefined(document.documentElement) || !document.documentElement.contains(domNode)) {
    return null;
  }

  var matches = isFunction(domNode.matches) ? 'matches' : Cast('msMatchesSelector');

  do {
    var _domNode$parentElemen;

    if (isFunction(domNode[matches]) && domNode[matches](selector)) {
      return domNode;
    }

    domNode = (_domNode$parentElemen = domNode.parentElement) !== null && _domNode$parentElemen !== void 0 ? _domNode$parentElemen : domNode.parentNode;
  } while (isElementDomNode(domNode));

  return null;
}
/**
 * Checks for a text node.
 *
 * @param domNode - the dom node
 *
 * @public
 */

function isTextDomNode(domNode) {
  return isDomNode(domNode) && domNode.nodeType === Node.TEXT_NODE;
}
/**
 * Retrieve the line height from a an element
 *
 * @param params - the element params
 */

function getLineHeight(_ref2) {
  var element = _ref2.element;
  return Number.parseFloat(window.getComputedStyle(element).lineHeight || '');
}
/**
 * Retrieve the nearest non-text node
 *
 * @param domNode - the dom node
 */

function getNearestNonTextNode(domNode) {
  return isTextDomNode(domNode) ? domNode.parentNode : domNode;
}
/**
 * Checks whether the cursor is at the end of the state.doc
 *
 * @param state - the editor state
 */

function atDocEnd(state) {
  return state.doc.nodeSize - state.selection.$to.pos - 2 === state.selection.$to.depth;
}
/**
 * Checks whether the cursor is at the beginning of the state.doc
 *
 * @param state - the editor state
 */

function atDocStart(state) {
  return state.selection.$from.pos === state.selection.$from.depth;
}
/**
 * Get the start position of the parent of the current resolve position
 *
 * @param pmPosition - the resolved prosemirror position
 */

function startPositionOfParent(pmPosition) {
  return pmPosition.start(pmPosition.depth);
}
/**
 * Get the end position of the parent of the current resolve position
 *
 * @param pmPosition - the resolved prosemirror position
 *
 * @public
 */

function endPositionOfParent(pmPosition) {
  return pmPosition.end(pmPosition.depth) + 1;
}
/**
 * Retrieve the current position of the cursor
 *
 * @param selection - the editor selection
 * @returns a resolved position only when the selection is a text selection
 *
 * @public
 */

function getCursor(selection) {
  return isTextSelection(selection) ? selection.$cursor : undefined;
}
/**
 * Checks to see whether a nodeMatch checker is a tuple
 *
 * @remarks
 * A node matcher can either be a string, a function a regex or a regex tuple.
 * This check for the latter two.
 *
 * @param nodeMatch - the node match
 *
 * @public
 */

function isRegexTuple(nodeMatch) {
  return Array.isArray(nodeMatch) && nodeMatch.length > 0 && nodeMatch.length <= 2;
}
/**
 * Test the passed in regexp tuple
 *
 * @param tuple - a regex tuple
 * @param value - the string to test against
 *
 * @public
 */


function regexTest(tuple, value) {
  var regex = new RegExp(...tuple);
  return regex.test(value);
}
/**
 * Checks to see whether the name of the passed node matches anything in the
 * list provided.
 *
 * @param node - the prosemirror node
 * @param nodeMatches - the list of possible matching nodes
 * @returns true if the node name is a match to any of the items in the
 * nodeMatches array
 *
 * @public
 */


function nodeNameMatchesList(node, nodeMatches) {
  var outcome = false;

  if (!node) {
    return outcome;
  }

  var name = node.type.name;

  var _iterator = _createForOfIteratorHelper(nodeMatches),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var checker = _step.value;
      outcome = isRegexTuple(checker) ? regexTest(checker, name) : isFunction(checker) ? checker(name, node) : checker === name;

      if (outcome) {
        return outcome;
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return outcome;
}
/**
 * Checks whether a Prosemirror node is the top level `doc` node
 *
 * @param node - the prosemirror node
 * @param schema - the prosemirror schema
 *
 * @public
 */

function isDocNode(node, schema) {
  return isProsemirrorNode(node) && (schema ? node.type === schema.nodes.doc : node.type.name === 'doc');
}
/**
 * Checks whether the passed in JSON is a valid object node
 *
 * @param value - the value to check
 *
 * @public
 */

function isRemirrorJSON(value) {
  return isObject(value) && value.type === 'doc' && Array.isArray(value.content);
}

/**
 * Get a valid selection from the primitive selection.
 */
function getTextSelection(selection, doc) {
  var max = doc.nodeSize - 2;
  var min = 0;
  var pos;
  /** Ensure the selection is within the current document range */

  var clampToDocument = value => clamp({
    min,
    max,
    value
  });

  if (isSelection(selection)) {
    return selection;
  }

  if (selection === 'start') {
    pos = min;
  } else if (selection === 'end') {
    pos = max;
  } else {
    pos = selection;
  }

  if (isNumber(pos)) {
    pos = clampToDocument(pos);
    return TextSelection.near(doc.resolve(pos));
  }

  var start = clampToDocument(pos.from);
  var end = clampToDocument(pos.to);
  return TextSelection.create(doc, start, end);
}

function createDocumentErrorHandler(parameter) {
  var onError = parameter.onError,
      schema = parameter.schema,
      error = parameter.error;

  if (isFunction(onError)) {
    return createFallback(onError(error), schema);
  }

  return createFallback(onError, schema);
}

function createFallback(fallback, schema) {
  return isProsemirrorNode(fallback) ? fallback : schema.nodeFromJSON(fallback);
}
/**
 * Creates a document node from the passed in content and schema.
 *
 * @param parameter - the destructured create document node params
 *
 * @public
 */


function createDocumentNode(parameter) {
  var _parameter$onError, _schema$nodes$doc$spe;

  var content = parameter.content,
      schema = parameter.schema,
      doc = parameter.doc,
      stringHandler = parameter.stringHandler;
  var onError = ((_parameter$onError = parameter.onError) !== null && _parameter$onError !== void 0 ? _parameter$onError : (_schema$nodes$doc$spe = schema.nodes.doc.spec.content) === null || _schema$nodes$doc$spe === void 0 ? void 0 : _schema$nodes$doc$spe.startsWith('block')) ? () => EMPTY_PARAGRAPH_NODE : () => EMPTY_NODE;

  if (isProsemirrorNode(content)) {
    return content;
  }

  if (isRemirrorJSON(content)) {
    try {
      return schema.nodeFromJSON(content);
    } catch (error) {
      return createDocumentErrorHandler({
        onError,
        schema,
        error
      });
    }
  }

  if (isString(content) && stringHandler) {
    return stringHandler({
      doc,
      content,
      schema
    });
  }

  return createDocumentErrorHandler({
    onError,
    schema,
    error: RemirrorError.create({
      message: 'Unrecognized content type'
    })
  });
}
/**
 * Checks which environment should be used. Returns true when we are in the dom
 * environment.
 *
 * @param forceEnvironment - force a specific environment to override the
 * outcome
 */

function shouldUseDomEnvironment(forceEnvironment) {
  return forceEnvironment === 'dom' || environment.isBrowser && !forceEnvironment;
}
/**
 * Retrieves the document based on the environment we are currently in.
 *
 * @param forceEnvironment - force a specific environment
 */

function getDocument(forceEnvironment) {
  {
    return document;
  }
}

/**
 * Convert a node into its DOM representative
 *
 * @param params - the from node params
 *
 * @public
 */
function toDom(_ref3) {
  var node = _ref3.node,
      schema = _ref3.schema,
      doc = _ref3.doc;
  var fragment = isDocNode(node, schema) ? node.content : Fragment.from(node);
  return DOMSerializer.fromSchema(schema).serializeFragment(fragment, {
    document: doc
  });
}

/**
 * Convert a prosemirror node into it's HTML contents
 *
 * @param params - the from node params
 *
 * @public
 */
function toHtml(_ref4) {
  var node = _ref4.node,
      schema = _ref4.schema,
      _ref4$doc = _ref4.doc,
      doc = _ref4$doc === void 0 ? getDocument() : _ref4$doc;
  var element = doc.createElement('div');
  element.append(toDom({
    node,
    schema,
    doc
  }));
  return element.innerHTML;
}

/**
 * Convert a HTML string into Prosemirror node
 *
 * @param params - the from html params
 *
 * @public
 */
function fromHtml(parameter) {
  var content = parameter.content,
      schema = parameter.schema,
      _parameter$doc = parameter.doc,
      doc = _parameter$doc === void 0 ? getDocument() : _parameter$doc;
  var element = doc.createElement('div');
  element.innerHTML = content.trim();
  return DOMParser.fromSchema(schema).parse(element);
}
/**
 * A wrapper around `state.doc.toJSON` which returns the state as a
 * `RemirrorJSON` object.
 */

function getRemirrorJSON(state) {
  return state.doc.toJSON();
}

/**
 * Check if two states are equal.
 */
function isStateEqual(stateA, stateB) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  if (stateA === stateB) {
    return true;
  }

  if (!stateA.doc.eq(stateB.doc)) {
    return false;
  }

  if (options.checkSelection && !stateA.selection.eq(stateB.selection)) {
    return false;
  }

  return true;
}
/**
 * Check that the nodes and marks present on schemaA are also present on schemaB.
 */

function areSchemaCompatible(schemaA, schemaB) {
  if (schemaA === schemaB) {
    return true;
  }

  var marksA = keys(schemaA.marks);
  var marksB = keys(schemaB.marks);
  var nodesA = keys(schemaA.nodes);
  var nodesB = keys(schemaB.nodes);

  if (marksA.length !== marksB.length || nodesA.length !== nodesB.length) {
    return false;
  }

  var _iterator2 = _createForOfIteratorHelper(marksA),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var mark = _step2.value;

      // No reverse check needed since we know the keys are unique and the lengths
      // are identical.
      if (!marksB.includes(mark)) {
        return false;
      }
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  var _iterator3 = _createForOfIteratorHelper(nodesA),
      _step3;

  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var node = _step3.value;

      // No reverse check needed since we know the keys are unique and the lengths
      // are identical.
      if (!nodesB.includes(node)) {
        return false;
      }
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }

  return true;
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _createForOfIteratorHelper$1(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray$1(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

/**
 * Checks if the type a given `node` has a given `nodeType`.
 */
function isNodeOfType(parameter) {
  var types = parameter.types,
      node = parameter.node;

  if (!node) {
    return false;
  }

  var matches = type => {
    return type === node.type || type === node.type.name;
  };

  if (isArray(types)) {
    return types.some(matches);
  }

  return matches(types);
}

/**
 * Creates a new transaction object from a given transaction. This is useful
 * when applying changes to a transaction, that you may want to rollback.
 *
 * ```ts
 * function() applyUpdateIfValid(state: EditorState) {
 *   const tr = cloneTransaction(state.tr);
 *
 *   tr.insertText('hello');
 *
 *   if (!checkValid(tr)) {
 *     return;
 *   }
 *
 *   applyClonedTransaction({ clone: tr, tr: state.tr });
 * }
 * ```
 *
 * The above example applies a transaction to the cloned transaction then checks
 * to see if the changes are still valid and if they are applies the mutative
 * changes to the original state transaction.
 *
 * @param tr - the prosemirror transaction
 *
 * @public
 */
function cloneTransaction(tr) {
  return Object.assign(Object.create(tr), tr).setTime(Date.now());
}

/**
 * Get the diff between two ordered arrays with a reference equality check.
 */
function diff(primary, other) {
  return primary.filter((item, index) => item !== other[index]);
}
/**
 * Apply the steps of a cloned transaction to the original transaction `tr`.
 */


function applyClonedTransaction(parameter) {
  var clone = parameter.clone,
      tr = parameter.tr;
  var steps = diff(clone.steps, tr.steps);

  var _iterator = _createForOfIteratorHelper$1(steps),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var step = _step.value;
      tr.step(step);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
}
/**
 * Checks if the type a given `node` has a given `nodeType`.
 */

function markEqualsType(parameter) {
  var types = parameter.types,
      mark = parameter.mark;
  return mark ? Array.isArray(types) && types.includes(mark.type) || mark.type === types : false;
}

/**
 * Performs a `delete` transaction that removes a node at a given position with
 * the given `node`. `position` should point at the position immediately before
 * the node.
 *
 * @param position - the prosemirror position
 *
 * @public
 */
function removeNodeAtPosition(_ref) {
  var pos = _ref.pos,
      tr = _ref.tr;
  var node = tr.doc.nodeAt(pos);

  if (node) {
    tr.delete(pos, pos + node.nodeSize);
  }

  return tr;
}

/**
 * Replaces the node at the provided position with the provided content.
 */
function replaceNodeAtPosition(_ref2) {
  var pos = _ref2.pos,
      tr = _ref2.tr,
      content = _ref2.content;
  var node = tr.doc.nodeAt(pos);

  if (node) {
    tr.replaceWith(pos, pos + node.nodeSize, content);
  }

  return tr;
}
/**
 * Returns DOM reference of a node at a given `position`.
 *
 * @remarks
 *
 * If the node type is of type `TEXT_NODE` it will return the reference of the
 * parent node.
 *
 * A simple use case
 *
 * ```ts
 * const element = findElementAtPosition($from.pos, view);
 * ```
 *
 * @param position - the prosemirror position
 * @param view - the editor view
 *
 * @public
 */

function findElementAtPosition(position, view) {
  var dom = view.domAtPos(position);
  var node = dom.node.childNodes[dom.offset];

  if (isTextDomNode(dom.node)) {
    return dom.node.parentNode;
  }

  if (isNullOrUndefined(node) || isTextDomNode(node)) {
    return dom.node;
  }

  return node;
}
/**
 * Iterates over parent nodes, returning the closest node and its start position
 * that the `predicate` returns truthy for. `start` points to the start position
 * of the node, `pos` points directly before the node.
 *
 * ```ts
 * const predicate = node => node.type === schema.nodes.blockquote;
 * const parent = findParentNode({predicate, selection});
 * ```
 */

function findParentNode(parameter) {
  var predicate = parameter.predicate,
      selection = parameter.selection;
  var $from = selection.$from;

  for (var depth = $from.depth; depth > 0; depth--) {
    var node = $from.node(depth);

    if (predicate(node)) {
      var pos = depth > 0 ? $from.before(depth) : 0;
      var start = $from.start(depth);
      var end = pos + node.nodeSize;
      return {
        pos,
        depth,
        node,
        start,
        end
      };
    }
  }

  return;
}
/**
 * Finds the node at the resolved position.
 *
 * @param $pos - the resolve position in the document
 */

function findNodeAtPosition($pos) {
  var depth = $pos.depth;
  var pos = depth > 0 ? $pos.before(depth) : 0;
  var node = $pos.node(depth);
  var start = $pos.start(depth);
  var end = pos + node.nodeSize;
  return {
    pos,
    start,
    node,
    end,
    depth
  };
}
/**
 * Finds the node at the passed selection.
 */

function findNodeAtSelection(selection) {
  var parentNode = findParentNode({
    predicate: () => true,
    selection
  });
  !parentNode ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: 'No parent node found for the selection provided.'
  }) : invariant(false) : void 0;
  return parentNode;
}

/**
 *  Iterates over parent nodes, returning closest node of a given `nodeType`.
 *  `start` points to the start position of the node, `pos` points directly
 *  before the node.
 *
 *  ```ts
 *  const parent = findParentNodeOfType({types: schema.nodes.paragraph, selection});
 *  ```
 */
function findParentNodeOfType(parameter) {
  var types = parameter.types,
      selection = parameter.selection;
  return findParentNode({
    predicate: node => isNodeOfType({
      types,
      node
    }),
    selection
  });
}
/**
 * Returns position of the previous node.
 *
 * ```ts
 * const pos = findPositionOfNodeBefore(tr.selection);
 * ```
 *
 * @param selection - the prosemirror selection
 */

function findPositionOfNodeBefore(value) {
  var $pos = isResolvedPos(value) ? value : isSelection(value) ? value.$from : value.selection.$from;

  if (isNullOrUndefined($pos)) {
    throw new Error('Invalid value passed in.');
  }

  var nodeBefore = $pos.nodeBefore;
  var selection = Selection.findFrom($pos, -1);

  if (!selection || !nodeBefore) {
    return;
  }

  var parent = findParentNodeOfType({
    types: nodeBefore.type,
    selection
  });
  return parent ? parent : {
    node: nodeBefore,
    pos: selection.$from.pos,
    end: selection.$from.end(),
    depth: selection.$from.depth + 1,
    start: selection.$from.start(selection.$from.depth + 1)
  };
}
/**
 * Updates the provided transaction to remove the node before.
 *
 * ```ts
 * dispatch(
 *    removeNodeBefore(state.tr)
 * );
 * ```
 *
 * @param tr
 *
 * @public
 */

function removeNodeBefore(tr) {
  var result = findPositionOfNodeBefore(tr.selection);

  if (result) {
    removeNodeAtPosition({
      pos: result.pos,
      tr
    });
  }

  return tr;
}

/**
 * Returns a node of a given `nodeType` if it is selected. `start` points to the
 * start position of the node, `pos` points directly before the node.
 *
 * ```ts
 * const { extension, inlineExtension, bodiedExtension } = schema.nodes;
 *
 * const selectedNode = findSelectedNodeOfType({
 *   types: [extension, inlineExtension, bodiedExtension],
 *   selection,
 * });
 * ```
 */
function findSelectedNodeOfType(parameter) {
  var types = parameter.types,
      selection = parameter.selection;

  if (!isNodeSelection(selection) || !isNodeOfType({
    types,
    node: selection.node
  })) {
    return;
  }

  return {
    pos: selection.$from.pos,
    depth: selection.$from.depth,
    start: selection.$from.start(),
    end: selection.$from.pos + selection.node.nodeSize,
    node: selection.node
  };
}

/**
 * Returns the position of the node after the current position, selection or
 * state.
 *
 * ```ts
 * const pos = findPositionOfNodeBefore(tr.selection);
 * ```
 *
 * @param selection - the prosemirror selection
 */
function findPositionOfNodeAfter(value) {
  var $pos = isResolvedPos(value) ? value : isSelection(value) ? value.$from : value.selection.$from;

  if (isNullOrUndefined($pos)) {
    throw new Error('Invalid value passed in.');
  }

  var nodeAfter = $pos.nodeAfter;
  var selection = Selection.findFrom($pos, 1);

  if (!selection || !nodeAfter) {
    return;
  }

  var parent = findParentNodeOfType({
    types: nodeAfter.type,
    selection
  });
  return parent ? parent : {
    node: nodeAfter,
    pos: selection.$from.pos,
    end: selection.$from.end(),
    depth: selection.$from.depth + 1,
    start: selection.$from.start(selection.$from.depth + 1)
  };
}
/**
 * Update the transaction to delete the node after the current selection.
 *
 * ```ts
 * dispatch(removeNodeBefore(state.tr));
 * ```
 *
 * @param tr
 *
 * @public
 */

function removeNodeAfter(tr) {
  var result = findPositionOfNodeAfter(tr.selection);

  if (result) {
    removeNodeAtPosition({
      pos: result.pos,
      tr
    });
  }

  return tr;
}
/**
 * Checks whether the selection or state is currently empty.
 *
 * @param value - the transaction selection or state
 */

function isSelectionEmpty(value) {
  if (isSelection(value)) {
    return value.empty;
  }

  if (isTransaction(value) || isEditorState(value)) {
    return value.selection.empty;
  }

  return true;
}
/**
 * Check to see if a transaction has changed either the document or the current
 * selection.
 *
 * @param tr - the transaction to check
 */

function hasTransactionChanged(tr) {
  return tr.docChanged || tr.selectionSet;
}

/**
 * Checks whether the node type passed in is active within the region. Used by
 * extensions to implement the `active` method.
 *
 * To ignore `attrs` just leave the attrs object empty or undefined.
 *
 * @param params - the destructured node active parameters
 */
function isNodeActive(parameter) {
  var _findSelectedNodeOfTy;

  var state = parameter.state,
      type = parameter.type,
      _parameter$attrs = parameter.attrs,
      attrs = _parameter$attrs === void 0 ? {} : _parameter$attrs;
  var selection = state.selection;

  var predicate = node => node.type === type;

  var parent = (_findSelectedNodeOfTy = findSelectedNodeOfType({
    selection,
    types: type
  })) !== null && _findSelectedNodeOfTy !== void 0 ? _findSelectedNodeOfTy : findParentNode({
    predicate,
    selection
  });

  if (!attrs || isEmptyObject(attrs) || !parent) {
    return bool(parent);
  }

  return parent.node.hasMarkup(type, _objectSpread(_objectSpread({}, parent.node.attrs), attrs));
}

/**
 * Converts a schema to a simple json compatible object.
 */
function schemaToJSON(schema) {
  var nodes = object();
  var marks = object();

  var _iterator2 = _createForOfIteratorHelper$1(entries(schema.nodes)),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var _step2$value = _slicedToArray(_step2.value, 2),
          key = _step2$value[0],
          spec = _step2$value[1].spec;

      nodes[key] = spec;
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  var _iterator3 = _createForOfIteratorHelper$1(entries(schema.marks)),
      _step3;

  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var _step3$value = _slicedToArray(_step3.value, 2),
          _key = _step3$value[0],
          _spec = _step3$value[1].spec;

      marks[_key] = _spec;
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }

  return {
    nodes,
    marks
  };
}
/**
 * Wraps the default {@link ProsemirrorCommandFunction} and makes it compatible
 * with the default **remirror** {@link CommandFunction} call signature.
 *
 * By default this is non chainable since it uses the `state.tr` rather than the
 * accumulated `tr` property passed into all command functions.
 */

function convertCommand(commandFunction) {
  return nonChainable((_ref3) => {
    var state = _ref3.state,
        dispatch = _ref3.dispatch,
        view = _ref3.view;
    return commandFunction(state, dispatch, view);
  });
}
/**
 * Brands a command as non chainable so that it can be excluded from the
 * inferred chainable commands.
 */

/**
 * Marks a command function as non chainable. It will throw an error when
 * chaining is attempted.
 *
 * @remarks
 *
 * ```ts
 * const command = nonChainable(({ state, dispatch }) => {...});
 * ```
 */
function nonChainable(commandFunction) {
  return parameter => {
    var _parameter$view;

    !(parameter.dispatch === undefined || parameter.dispatch === ((_parameter$view = parameter.view) === null || _parameter$view === void 0 ? void 0 : _parameter$view.dispatch)) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.NON_CHAINABLE_COMMAND
    }) : invariant(false) : void 0;
    return commandFunction(parameter);
  };
}
/**
 * Similar to the chainCommands from the `prosemirror-commands` library. Allows
 * multiple commands to be chained together and runs until one of them returns
 * true.
 */

function chainCommands() {
  for (var _len = arguments.length, commands = new Array(_len), _key2 = 0; _key2 < _len; _key2++) {
    commands[_key2] = arguments[_key2];
  }

  return (_ref4) => {
    var state = _ref4.state,
        dispatch = _ref4.dispatch,
        view = _ref4.view,
        tr = _ref4.tr,
        rest = _objectWithoutProperties(_ref4, ["state", "dispatch", "view", "tr"]);

    var _iterator4 = _createForOfIteratorHelper$1(commands),
        _step4;

    try {
      for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
        var element = _step4.value;

        if (element(_objectSpread({
          state,
          dispatch,
          view,
          tr
        }, rest))) {
          return true;
        }
      }
    } catch (err) {
      _iterator4.e(err);
    } finally {
      _iterator4.f();
    }

    return false;
  };
}
/**
 * Chains together keybindings, allowing for the sme key binding to be used
 * across multiple extensions without overriding behavior.
 *
 * @remarks
 *
 * When `next` is called it hands over full control of the keybindings to the
 * function that invokes it.
 */

function chainKeyBindingCommands() {
  for (var _len2 = arguments.length, commands = new Array(_len2), _key3 = 0; _key3 < _len2; _key3++) {
    commands[_key3] = arguments[_key3];
  }

  return parameters => {
    // When no commands are passed just ignore and continue.
    if (isEmptyArray(commands)) {
      return false;
    }

    var command = commands[0],
        rest = commands.slice(1); // Keeps track of whether the `next` method has been called. If it has been
    // called we return the result and skip the rest of the downstream commands.

    var calledNext = false;
    /**
     * Create the next function call. Updates the outer closure when the next
     * method has been called.
     */

    var createNext = function createNext() {
      for (var _len3 = arguments.length, nextCommands = new Array(_len3), _key4 = 0; _key4 < _len3; _key4++) {
        nextCommands[_key4] = arguments[_key4];
      }

      return () => {
        if (isEmptyArray(nextCommands)) {
          return false;
        } // Update the closure with information that the next method was invoked by
        // this command.


        calledNext = true;
        var nextRest = nextCommands.slice(1); // Recursively call the key bindings method.

        return chainKeyBindingCommands(...nextCommands)(_objectSpread(_objectSpread({}, parameters), {}, {
          next: createNext(...nextRest)
        }));
      };
    };

    var next = createNext(...rest);
    var exitEarly = command(_objectSpread(_objectSpread({}, parameters), {}, {
      next
    })); // Exit the chain of commands early if either:
    // - a) next was called
    // - b) the command returned true

    if (calledNext || exitEarly) {
      return exitEarly;
    } // Continue on through the chain of commands.


    return next();
  };
}

function mergeKeyBindingCreator(extensionKeymaps, mapper) {
  var previousCommandsMap = new Map();
  var mappedCommands = object();

  var _iterator5 = _createForOfIteratorHelper$1(extensionKeymaps),
      _step5;

  try {
    for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
      var extensionKeymap = _step5.value;

      var _iterator6 = _createForOfIteratorHelper$1(keys(extensionKeymap)),
          _step6;

      try {
        for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
          var _previousCommandsMap$;

          var key = _step6.value;
          var previousCommands = (_previousCommandsMap$ = previousCommandsMap.get(key)) !== null && _previousCommandsMap$ !== void 0 ? _previousCommandsMap$ : [];
          var commands = [...previousCommands, extensionKeymap[key]];

          var _command = chainKeyBindingCommands(...commands);

          previousCommandsMap.set(key, commands);
          mappedCommands[key] = mapper(_command);
        }
      } catch (err) {
        _iterator6.e(err);
      } finally {
        _iterator6.f();
      }
    }
  } catch (err) {
    _iterator5.e(err);
  } finally {
    _iterator5.f();
  }

  return mappedCommands;
}
/**
 * This merges an array of keybindings into one keybinding with the priority
 * given to the items earlier in the array. `index: 0` has priority over `index: 1`
 * which has priority over `index: 2` and so on.
 *
 * This is for use on remirror keybindings. See `mergeProsemirrorKeyBindings`
 * for transforming the methods into `ProsemirrorCommandFunction`'s.
 */


function mergeKeyBindings(extensionKeymaps) {
  return mergeKeyBindingCreator(extensionKeymaps, command => command);
}
/**
 * This merges an array of keybindings into one keybinding with the priority
 * given to the items earlier in the array. `index: 0` has priority over `index: 1`
 * which has priority over `index: 2` and so on.
 */

function mergeProsemirrorKeyBindings(extensionKeymaps) {
  return mergeKeyBindingCreator(extensionKeymaps, command => (state, dispatch, view) => {
    return command({
      state,
      dispatch,
      view,
      tr: state.tr,
      next: () => false
    });
  });
}

function _createForOfIteratorHelper$2(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray$2(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray$2(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$2(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen); }

function _arrayLikeToArray$2(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

/**
 * Update the selection with the provided MarkType.
 */
function updateMark(parameter) {
  return (_ref) => {
    var dispatch = _ref.dispatch,
        tr = _ref.tr;
    var type = parameter.type,
        _parameter$attrs = parameter.attrs,
        attrs = _parameter$attrs === void 0 ? object() : _parameter$attrs,
        appendText = parameter.appendText,
        range = parameter.range;
    var selection = tr.selection;

    var _ref2 = range !== null && range !== void 0 ? range : selection,
        from = _ref2.from,
        to = _ref2.to;

    if (!dispatch) {
      return true;
    }

    tr.addMark(from, to, type.create(attrs));

    if (appendText) {
      tr.insertText(appendText);
    }

    dispatch(tr);
    return true;
  };
}
/**
 * Lift the selected block, or the closest ancestor block of the selection that
 * can be lifted, out of its parent node.
 *
 * Adapted from
 * https://github.com/ProseMirror/prosemirror-commands/blob/3126d5c625953ba590c5d3a0db7f1009f46f1571/src/commands.js#L212-L221
 */

function lift(_ref3) {
  var tr = _ref3.tr,
      dispatch = _ref3.dispatch;
  var _tr$selection = tr.selection,
      $from = _tr$selection.$from,
      $to = _tr$selection.$to;
  var range = $from.blockRange($to);
  var target = range && liftTarget(range);

  if (!isNumber(target) || !range) {
    return false;
  }

  dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.lift(range, target).scrollIntoView());
  return true;
}
/**
 * Wrap the selection in a node of the given type with the given attributes.
 */

function wrapIn(type, attrs) {
  return function (_ref4) {
    var tr = _ref4.tr,
        dispatch = _ref4.dispatch;
    var _tr$selection2 = tr.selection,
        $from = _tr$selection2.$from,
        $to = _tr$selection2.$to;
    var range = $from.blockRange($to);
    var wrapping = range && findWrapping(range, type, attrs);

    if (!wrapping || !range) {
      return false;
    }

    dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.wrap(range, wrapping).scrollIntoView());
    return true;
  };
}
/**
 * Toggle between wrapping an inactive node with the provided node type, and
 * lifting it up into it's parent.
 *
 * @param type - the node type to toggle
 * @param attrs - the attrs to use for the node
 *
 * @public
 */

function toggleWrap(type) {
  var attrs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return parameter => {
    var tr = parameter.tr;
    var isActive = isNodeActive({
      state: tr,
      type
    });

    if (isActive) {
      return lift(parameter);
    }

    return wrapIn(type, attrs)(parameter);
  };
}
/**
 * Returns a command that tries to set the selected textblocks to the
 * given node type with the given attributes.
 */

function setBlockType(type, attrs) {
  return function (_ref5) {
    var tr = _ref5.tr,
        dispatch = _ref5.dispatch;
    var _tr$selection3 = tr.selection,
        from = _tr$selection3.from,
        to = _tr$selection3.to;
    var applicable = false;
    tr.doc.nodesBetween(from, to, (node, pos) => {
      if (applicable) {
        return false;
      }

      if (!node.isTextblock || node.hasMarkup(type, attrs)) {
        return;
      }

      if (node.type === type) {
        applicable = true;
        return;
      }

      var $pos = tr.doc.resolve(pos);
      var index = $pos.index();
      applicable = $pos.parent.canReplaceWith(index, index + 1, type);
      return;
    });

    if (!applicable) {
      return false;
    }

    if (dispatch) {
      dispatch(tr.setBlockType(from, to, type, attrs).scrollIntoView());
    }

    return true;
  };
}
/**
 * TODO make this more inclusive of custom user list types.
 */

function isList(node, schema) {
  return node.type === schema.nodes.bulletList || node.type === schema.nodes.orderedList;
}
/**
 * Toggles a list item.
 *
 * @remarks
 *
 * When the provided list wrapper is inactive (e.g. ul) then wrap the list with
 * this type. When it is active then remove the selected line from the list.
 *
 * @param type - the list node type
 * @param itemType - the list item type (must be in the schema)
 *
 * @public
 */


function toggleList(type, itemType) {
  return parameter => {
    var state = parameter.state,
        dispatch = parameter.dispatch,
        tr = parameter.tr;
    var schema = state.schema;
    var selection = tr.selection;
    var $from = selection.$from,
        $to = selection.$to;
    var range = $from.blockRange($to);

    if (!range) {
      return false;
    }

    var parentList = findParentNode({
      predicate: node => isList(node, schema),
      selection
    });

    if (range.depth >= 1 && parentList && range.depth - parentList.depth <= 1) {
      if (parentList.node.type === type) {
        return liftListItem(itemType)(state, dispatch);
      }

      if (isList(parentList.node, schema) && type.validContent(parentList.node.content)) {
        tr.setNodeMarkup(parentList.pos, type);

        if (dispatch) {
          dispatch(tr);
        }

        return true;
      }
    }

    return wrapInList(type)(state, dispatch);
  };
}

/**
 * Toggle a block between the provided type and toggleType.
 *
 * @param params - the destructured params
 *
 * @public
 */
function toggleBlockItem(toggleParameter) {
  return parameter => {
    var tr = parameter.tr;
    var type = toggleParameter.type,
        toggleType = toggleParameter.toggleType,
        attrs = toggleParameter.attrs;
    var isActive = isNodeActive({
      state: tr,
      type,
      attrs
    });

    if (isActive) {
      return setBlockType(toggleType)(parameter);
    }

    return setBlockType(type, attrs)(parameter);
  };
}

/**
 * Taken from https://stackoverflow.com/a/4900484
 *
 * Check that the browser is chrome. Supports passing a minimum version to check
 * that it is a greater than or equal version.
 */
function isChrome() {
  var minVersion = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var parsedAgent = navigator.userAgent.match(/Chrom(e|ium)\/(\d+)\./);
  return parsedAgent ? Number.parseInt(parsedAgent[2], 10) >= minVersion : false;
}

function keepSelectionTransaction(view, tr) {
  var from = view.state.selection.from;

  var _iterator = _createForOfIteratorHelper$2(tr.steps),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var step = _step.value;
      var map = step.getMap();
      from = map.map(from);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  tr.setSelection(new TextSelection(tr.doc.resolve(from)));
}
/**
 * Replaces text with an optional appended string at the end
 *
 * @param params - the destructured params
 *
 * @public
 */


function replaceText(parameter) {
  var range = parameter.range,
      type = parameter.type,
      _parameter$attrs2 = parameter.attrs,
      attrs = _parameter$attrs2 === void 0 ? {} : _parameter$attrs2,
      _parameter$appendText = parameter.appendText,
      appendText = _parameter$appendText === void 0 ? '' : _parameter$appendText,
      _parameter$content = parameter.content,
      content = _parameter$content === void 0 ? '' : _parameter$content,
      _parameter$keepSelect = parameter.keepSelection,
      keepSelection = _parameter$keepSelect === void 0 ? false : _parameter$keepSelect;
  return (_ref6) => {
    var state = _ref6.state,
        tr = _ref6.tr,
        dispatch = _ref6.dispatch,
        view = _ref6.view;
    var schema = state.schema;
    var selection = tr.selection;
    var index = selection.$from.index();

    var _ref7 = range !== null && range !== void 0 ? range : selection,
        from = _ref7.from,
        to = _ref7.to;

    if (isNodeType(type)) {
      if (!selection.$from.parent.canReplaceWith(index, index, type)) {
        return false;
      }

      tr.replaceWith(from, to, type.create(attrs, content ? schema.text(content) : undefined));
    } else {
      !content ? process.env.NODE_ENV !== "production" ? invariant(false, {
        message: '`replaceText` cannot be called without content when using a mark type'
      }) : invariant(false) : void 0;
      tr.replaceWith(from, to, schema.text(content, isMarkType(type) ? [type.create(attrs)] : undefined));
    } // Only append the text if text is provided (ignore the empty string).


    if (appendText) {
      // TODO for some reason this only works when content follows current selection.
      tr.insertText(appendText);
    }

    if (keepSelection && view) {
      keepSelectionTransaction(view, tr);
    }

    if (dispatch) {
      // A workaround for a chrome bug
      // https://github.com/ProseMirror/prosemirror/issues/710#issuecomment-338047650
      if (isChrome(60)) {
        var _document$getSelectio;

        (_document$getSelectio = document.getSelection()) === null || _document$getSelectio === void 0 ? void 0 : _document$getSelectio.empty();
      }

      dispatch(tr);
    }

    return true;
  };
}

/**
 * Removes a mark from the current selection or provided range.
 *
 * @param params - the destructured params
 *
 * @public
 */
function removeMark(parameter) {
  return (_ref8) => {
    var dispatch = _ref8.dispatch,
        tr = _ref8.tr;
    var type = parameter.type,
        _parameter$expand = parameter.expand,
        expand = _parameter$expand === void 0 ? false : _parameter$expand,
        range = parameter.range;
    var selection = tr.selection;

    var _ref9 = range !== null && range !== void 0 ? range : selection,
        from = _ref9.from,
        to = _ref9.to;

    if (expand) {
      var _ref10 = range ? getMarkRange(tr.doc.resolve(range.from), type) || isNumber(range.to) && getMarkRange(tr.doc.resolve(range.to), type) || {
        from,
        to
      } : isSelectionEmpty(tr) ? getMarkRange(selection.$anchor, type) || {
        from,
        to
      } : {
        from,
        to
      };

      from = _ref10.from;
      to = _ref10.to;
    }

    tr.removeMark(from, isNumber(to) ? to : from, type);

    if (dispatch) {
      dispatch(tr);
    }

    return true;
  };
}
/**
 * An empty (noop) command function.
 *
 * @remarks
 *
 * This is typically used to represent a default _do nothing_ action.
 */

var emptyCommandFunction = () => false;

/**
 * Keycode for ALT key.
 */

var ALT = ['Alt', '⌥'];
/**
 * Keycode for CTRL key.
 */

var CTRL = ['Control', '⌃'];
/**
 * Keycode for COMMAND/META key.
 */

var COMMAND = ['Meta', '⌘'];
/**
 * Keycode for SHIFT key.
 */

var SHIFT = ['Shift', '⇧'];
/**
 * Keycode for the windows key
 */

var WINDOWS = ['Windows', '', '❖'];
/**
 * Keycode and symbols for caps lock
 */

var CAPS_LOCK = ['CapsLock', '⇪', '⇪'];
/**
 * Shorthand names for common modifier key combinations which should `just` work on Apple and PC.
 */

var Modifier = {
  Primary: isMac => isMac ? [COMMAND[0]] : [CTRL[0]],
  PrimaryShift: isMac => isMac ? [SHIFT[0], COMMAND[0]] : [CTRL[0], SHIFT[0]],
  PrimaryAlt: isMac => isMac ? [ALT[0], COMMAND[0]] : [CTRL[0], ALT[0]],
  Secondary: isMac => isMac ? [SHIFT[0], ALT[0], COMMAND[0]] : [CTRL[0], SHIFT[0], ALT[0]],
  Access: isMac => isMac ? [CTRL[0], ALT[0]] : [SHIFT[0], ALT[0]],
  Ctrl: () => [CTRL[0]],
  Alt: () => [ALT[0]],
  CtrlShift: () => [CTRL[0], SHIFT[0]],
  Shift: () => [SHIFT[0]],
  ShiftAlt: () => [SHIFT[0], ALT[0]]
};
/**
 * The cross platform modifier key combination names.
 */

/**
 * Returns true if this is an apple environment either on the server or the client.
 */
function isApple() {
  return environment.isApple;
}
/**
 * Create a consistent cross platform modifier string pattern.
 *
 * This can be used with the pressKeyWithModifier.
 *
 * ```ts
 * mod('Primary', 'A')
 * // => 'Control-A' // on PC
 * // => 'Cmd-A' // on Mac
 * ```
 *
 * @param modifier - A named modifier which is consistent across platforms
 *                  e.g. 'Primary' refers to 'Meta' on a mac and 'Control' on a PC
 * @param key - the key to press with the modifier. e.g. `Space` | `Enter`
 * @param [isApple] - a method which returns true when this is an apple device.
 */

function mod(modifier, key) {
  var isMacFn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : isApple;

  switch (modifier) {
    case 'Primary':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'PrimaryShift':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'PrimaryAlt':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'Secondary':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'Access':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'Ctrl':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'Alt':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'CtrlShift':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'Shift':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'ShiftAlt':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    default:
      throw new Error('Invalid modifier name passed in');
  }
}

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Flattens descendants of a given `node`.
 *
 * @remarks
 *
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const children = flatten(node);
 * ```
 */
function flattenNodeDescendants(parameter) {
  var node = parameter.node,
      _parameter$descend = parameter.descend,
      descend = _parameter$descend === void 0 ? true : _parameter$descend;
  !isProsemirrorNode(node) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.INTERNAL,
    message: 'Invalid "node" parameter".'
  }) : invariant(false) : void 0;
  var result = [];
  node.descendants((child, pos) => {
    result.push({
      node: child,
      pos
    });

    if (!descend) {
      return false; // Prevent diving into descendants.
    }

    return;
  });
  return result;
}

/**
 * Iterates over descendants of a given `node`, returning child nodes predicate returns truthy for.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const textNodes = findChildren(node, child => child.isText, false);
 * ```
 */
function findChildren(parameter) {
  var node = parameter.node,
      predicate = parameter.predicate,
      descend = parameter.descend;
  !node ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.INTERNAL,
    message: 'Invalid "node" parameter passed to "findChildren".'
  }) : invariant(false) : void 0;
  !isFunction(predicate) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.INTERNAL,
    message: 'Invalid "predicate" parameter passed to "findChildren".'
  }) : invariant(false) : void 0;
  return flattenNodeDescendants({
    node,
    descend
  }).filter(child => predicate(child.node));
}

function findNodeByPredicate(_ref) {
  var predicate = _ref.predicate;
  return parameters => findChildren(_objectSpread$1(_objectSpread$1({}, parameters), {}, {
    predicate
  }));
}
/**
 * Returns text nodes of a given `node`.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const textNodes = findTextNodes(node);
 * ```
 */


var findTextNodes = /*#__PURE__*/findNodeByPredicate({
  predicate: child => child.isText
});
/**
 * Returns inline nodes of a given `node`.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const inlineNodes = findInlineNodes(node);
 * ```
 */

var findInlineNodes = /*#__PURE__*/findNodeByPredicate({
  predicate: child => child.isInline
});
/**
 * Returns block descendants of a given `node`.
 *
 * @remarks
 *
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const blockNodes = findBlockNodes(node);
 * ```
 */

var findBlockNodes = /*#__PURE__*/findNodeByPredicate({
  predicate: child => child.isBlock
});

/**
 * Iterates over descendants of a given `node`, returning child nodes predicate returns truthy for.
 *
 * @remarks
 *
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const mergedCells = findChildrenByAttr(table, attrs => attrs.colspan === 2);
 * ```
 */
function findChildrenByAttribute(parameter) {
  var node = parameter.node,
      _predicate = parameter.predicate,
      descend = parameter.descend;
  return findChildren({
    node,
    predicate: child => _predicate(child.attrs),
    descend
  });
}

/**
 * Iterates over descendants of a given `node`, returning child nodes of a given nodeType.
 *
 * @remarks
 *
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const cells = findChildrenByNode(table, schema.nodes.tableCell);
 * ```
 */
function findChildrenByNode(parameter) {
  var node = parameter.node,
      type = parameter.type,
      descend = parameter.descend;
  return findChildren({
    node,
    predicate: child => child.type === type,
    descend
  });
}

/**
 * Iterates over descendants of a given `node`, returning child nodes that have a mark of a given markType.
 *
 * @remarks
 *
 * It doesn't descend into a `node` when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const nodes = findChildrenByMark(state.doc, schema.marks.strong);
 * ```
 */
function findChildrenByMark(paramter) {
  var node = paramter.node,
      type = paramter.type,
      descend = paramter.descend;
  return findChildren({
    node,
    predicate: child => bool(type.isInSet(child.marks)),
    descend
  });
}

/**
 * Returns `true` if a given node contains nodes of a given `nodeType`.
 *
 * @remarks
 *
 * ```ts
 * if (containsNodesOfType({ node: panel, type: schema.nodes.listItem })) {
 *   log('contained')
 * }
 * ```
 */
function containsNodesOfType(parameter) {
  var node = parameter.node,
      type = parameter.type;
  return findChildrenByNode({
    node,
    type
  }).length > 0;
}

/**
 * Creates a paste rule based on the provided regex for the provided mark type.
 *
 * TODO extract this into a separate package
 * - All contained in one plugin.
 * - Support for node paste rules
 * - Support for pasting different kinds of content.
 */
function markPasteRule(parameter) {
  var regexp = parameter.regexp,
      type = parameter.type,
      getAttributes = parameter.getAttributes;

  var handler = fragment => {
    var nodes = [];
    fragment.forEach(child => {
      if (child.isText) {
        var _child$text;

        var text = (_child$text = child.text) !== null && _child$text !== void 0 ? _child$text : '';
        var pos = 0;
        findMatches(text, regexp).forEach(match => {
          if (!match[1]) {
            return;
          }

          var start = match.index;
          var end = start + match[0].length;
          var attributes = isFunction(getAttributes) ? getAttributes(match) : getAttributes;

          if (start > 0) {
            nodes.push(child.cut(pos, start));
          }

          nodes.push(child.cut(start, end).mark(type.create(attributes).addToSet(child.marks)));
          pos = end;
        });

        if (text && pos < text.length) {
          nodes.push(child.cut(pos));
        }
      } else {
        nodes.push(child.copy(handler(child.content)));
      }
    });
    return Fragment.fromArray(nodes);
  };

  return new Plugin({
    key: new PluginKey('pasteRule'),
    props: {
      transformPasted: slice => new Slice(handler(slice.content), slice.openStart, slice.openEnd)
    }
  });
}
/**
 * Creates an input rule based on the provided regex for the provided mark type
 */

function markInputRule(parameter) {
  var regexp = parameter.regexp,
      type = parameter.type,
      getAttributes = parameter.getAttributes;
  return new InputRule(regexp, (state, match, start, end) => {
    var tr = state.tr;
    var attributes = isFunction(getAttributes) ? getAttributes(match) : getAttributes;
    var markEnd = end;

    if (match[1]) {
      var startSpaces = match[0].search(/\S/);
      var textStart = start + match[0].indexOf(match[1]);
      var textEnd = textStart + match[1].length;

      if (textEnd < end) {
        tr.delete(textEnd, end);
      }

      if (textStart > start) {
        tr.delete(start + startSpaces, textStart);
      }

      markEnd = start + startSpaces + match[1].length;
    }

    return tr.addMark(start, markEnd, type.create(attributes)) // Make sure not to continue with any ongoing marks
    .removeStoredMark(type);
  });
}
/**
 * Creates an node input rule based on the provided regex for the provided node type
 */

function nodeInputRule(parameter) {
  var regexp = parameter.regexp,
      type = parameter.type,
      getAttributes = parameter.getAttributes,
      _parameter$updateSele = parameter.updateSelection,
      updateSelection = _parameter$updateSele === void 0 ? false : _parameter$updateSele;
  return new InputRule(regexp, (state, match, start, end) => {
    var attributes = isFunction(getAttributes) ? getAttributes(match) : getAttributes;
    var tr = state.tr;
    tr.replaceWith(start - 1, end, type.create(attributes));

    if (updateSelection) {
      var $pos = tr.doc.resolve(start);
      tr.setSelection(new TextSelection($pos));
    }

    return tr;
  });
}
/**
 * Creates an node input rule based on the provided regex for the provided node type
 */

function plainInputRule(parameter) {
  var regexp = parameter.regexp,
      transformMatch = parameter.transformMatch,
      _parameter$updateSele2 = parameter.updateSelection,
      updateSelection = _parameter$updateSele2 === void 0 ? false : _parameter$updateSele2;
  return new InputRule(regexp, (state, match, start, end) => {
    var value = transformMatch(match);

    if (isNullOrUndefined(value)) {
      return null;
    }

    var tr = state.tr;

    if (value === '') {
      tr.delete(start, end);
    } else {
      tr.replaceWith(start, end, state.schema.text(value));
    }

    if (updateSelection) {
      var $pos = tr.doc.resolve(start);
      tr.setSelection(new TextSelection($pos));
    }

    return tr;
  });
}

export { ALT, CAPS_LOCK, COMMAND, CTRL, Modifier, SHIFT, WINDOWS, applyClonedTransaction, areSchemaCompatible, atDocEnd, atDocStart, canInsertNode, chainCommands, chainKeyBindingCommands, cloneTransaction, closestElement, containsNodesOfType, convertCommand, createDocumentNode, emptyCommandFunction, endPositionOfParent, environment, findBlockNodes, findChildren, findChildrenByAttribute, findChildrenByMark, findChildrenByNode, findElementAtPosition, findInlineNodes, findNodeAtPosition, findNodeAtSelection, findParentNode, findParentNodeOfType, findPositionOfNodeAfter, findPositionOfNodeBefore, findSelectedNodeOfType, findTextNodes, flattenNodeDescendants as flatten, fromHtml, getCursor, getDocument, getLineHeight, getMarkAttributes, getMarkRange, getMatchString, getNearestNonTextNode, getPluginMeta, getPluginState, getRemirrorJSON, getSelectedGroup, getSelectedWord, getTextContentFromSlice, getTextSelection, hasTransactionChanged, isApple, isDocNode, isDocNodeEmpty, isDomNode, isEditorSchema, isEditorState, isElementDomNode, isMarkActive, isMarkType, isNodeActive, isNodeOfType, isNodeSelection, isNodeType, isProsemirrorMark, isProsemirrorNode, isRemirrorJSON, isResolvedPos, isSelection, isSelectionEmpty, isStateEqual, isTextDomNode, isTextSelection, isTransaction, lift, markEqualsType, markInputRule, markPasteRule, mergeKeyBindings, mergeProsemirrorKeyBindings, mod, nodeInputRule, nodeNameMatchesList, nonChainable, plainInputRule, removeMark, removeNodeAfter, removeNodeAtPosition, removeNodeBefore, replaceNodeAtPosition, replaceText, schemaToJSON, setBlockType, setPluginMeta, shouldUseDomEnvironment, startPositionOfParent, toDom, toHtml, toggleBlockItem, toggleList, toggleWrap, updateMark, wrapIn };
